import { useState, useEffect } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import {
  Button, FileInput, Text, ProgressBar, Flex, Row, Modal, Dialog, toast,
} from '@oliasoft-open-source/react-ui-library';
import { uploadFormData, resetUploadStatus } from 'client/store/entities/upload/upload';
import { isEmpty } from 'es-toolkit/compat';
import { getAllNodes } from 'client/store/entities/node/node';
import { RecongnizebleFileFormats } from 'common/enums/known-data-enums';
import { useTranslation } from 'react-i18next';
import translations from 'client/internationalization/translation-map.json';
import { FileParsingStatus } from 'common/interfaces/objects-interfaces';
import { NodesModal } from '../nodes/node-modal';

export const supportedMimeTypes = {
  [RecongnizebleFileFormats.Edm]: 'text/xml',
  [RecongnizebleFileFormats.Pdf]: 'application/pdf',
  [RecongnizebleFileFormats.Excel]: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  image: 'image/',
};

const mimeToObjectType = (mime: string | undefined) => {
  switch (mime) {
    case supportedMimeTypes[RecongnizebleFileFormats.Edm]: return RecongnizebleFileFormats.Edm;
    case supportedMimeTypes[RecongnizebleFileFormats.Pdf]: return RecongnizebleFileFormats.Pdf;
    case supportedMimeTypes[RecongnizebleFileFormats.Excel]: return RecongnizebleFileFormats.Excel;
    default: {
      if (mime?.startsWith(supportedMimeTypes.image)) return 'image';
      return mime;
    }
  }
};

interface IFileUploadProps extends PropsFromRedux {
  archiveid: string,
  isModal?: boolean,
  initialNodeId?: string | null,
  visible?: boolean,
  onClose?: Function,
  initialFiles?: File[],
  onUploadComplete?: Function,
}

const FileUpload = ({
  uploadFormData, isUploading, sourceId, progress, nodes, getAllNodes, isLoading, isModal, initialNodeId, visible, onClose, initialFiles, onUploadComplete, parsingStatus,
  resetUploadStatus, archiveid,
}: IFileUploadProps) => {
  const [fileCount, setFileCount] = useState(0);
  const [fileNumber, setFileNumber] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [nodeListVisible, setNodeListVisible] = useState<boolean>(false);
  const [nodeId, setNodeId] = useState('');
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [isParsing, setIsParsing] = useState(false);

  const getNextFile = () => {
    const filesCopy = [...files];
    const file = filesCopy.pop();
    setFiles(filesCopy);
    return file;
  };

  const closeNodeList = () => {
    setNodeListVisible(false);
    onClose?.();
  };

  const onUploadProgress = (event: any) => {
    const percentage = Math.round((100 * event.loaded) / event.total);
    setUploadPercentage(percentage);
  };

  const uploadFile = (fileToUpload: File, nodeId: string, archiveid: string) => {
    const objectType = mimeToObjectType(fileToUpload.type);
    const formData = new FormData();
    formData.append('file', fileToUpload);
    setUploadPercentage(0);
    uploadFormData({
      data: formData,
      objectType: objectType!,
      nodeid: initialNodeId || nodeId,
      archiveid,
      onUploadProgress,
    });
  };

  const uploadNextFile = (nodeId: string, archiveid: string) => {
    const nextFile = getNextFile();
    if (nextFile) {
      setFileNumber(fileNumber + 1);
      uploadFile(nextFile, nodeId, archiveid);
      return true;
    }
    return false;
  };

  const { t } = useTranslation();

  const handleParsingSuccess = () => {
    setIsParsing(false);
    if (!uploadNextFile(nodeId, archiveid)) {
      setFiles([]);
      onClose?.();
      onUploadComplete?.();
      resetUploadStatus();

      if (fileCount > 1) {
        toast({
          message: {
            type: 'Success',
            icon: true,
            content: t(translations.importExport_parsinSuccessPlural, { number: fileCount }) || '',
            details: initialFiles?.map(file => file.name).join(', ') || '',
          },
          id: 'toast-upload-success',
        });
      } else {
        toast({
          message: {
            type: 'Success',
            icon: true,
            content: t(translations.importExport_parsinSuccess),
            heading: initialFiles?.[fileNumber - 1]?.name || '',
          },
          id: 'toast-upload-success',
        });
      }
    }
  };

  const handleParsingFailed = () => {
    setIsParsing(false);
    toast({
      message: {
        type: 'Error',
        icon: true,
        content: t(translations.importExport_parsingError),
      },
      id: 'toast-parsing-failed',
    });
    resetUploadStatus();
  };

  useEffect(() => {
    if (initialNodeId) {
      setNodeId(initialNodeId);
    }
  }, [initialNodeId]);

  useEffect(() => {
    if (nodeListVisible) {
      getAllNodes();
    }
  }, [nodeListVisible]);

  useEffect(() => {
    if (sourceId && !progress && !isParsing) {
      setIsParsing(true);
    }
  }, [sourceId]);

  useEffect(() => {
    if (parsingStatus === FileParsingStatus.SUCCESS) {
      handleParsingSuccess();
    }

    if (parsingStatus === FileParsingStatus.FAILED) {
      handleParsingFailed();
    }
  }, [parsingStatus]);

  const handleConfirmNode = (nodeId: string) => {
    setNodeListVisible(false);
    setNodeId(nodeId);
    uploadNextFile(nodeId, archiveid);
  };

  const onClickUpload = () => {
    setFileCount(files?.length);
    if (initialNodeId) {
      uploadNextFile(initialNodeId, archiveid);
    } else {
      setNodeListVisible(true);
    }
  };

  const closeUploadModal = () => {
    setFiles([]);
    setFileCount(0);
    setFileNumber(0);
    onClose?.();
  };

  useEffect(() => {
    if (initialFiles && !isEmpty(initialFiles) && initialNodeId) {
      setFileCount(initialFiles.length);
      setFileNumber(1);
      const firstFile = initialFiles[0];
      setFiles(initialFiles.slice(1));
      uploadFile(firstFile, initialNodeId, archiveid);
    }
  }, [initialFiles]);

  const nodeListModal = (
    <Flex>
      <NodesModal visible={nodeListVisible} onConfirmNodeSelect={handleConfirmNode} onClose={closeNodeList} getAllNodes={getAllNodes} isNodesLoading={isLoading} nodes={nodes} />
    </Flex>
  );

  const getPercentage = () => {
    if (isParsing) {
      return progress?.toFixed(2);
    }
    const percentage = Math.floor(((uploadPercentage) * 0.5 + (fileNumber - 1) * 100) / fileCount);
    return percentage;
  };

  const fileProgress = fileCount > 1 ? `(${fileNumber}/${fileCount})` : '';
  const currentFileName = initialFiles?.[fileNumber - 1]?.name || '';

  const uploadComponents = (
    <Flex justifyContent="center">
      {nodeListVisible && nodeListModal}
      {!isUploading && !isParsing && (
        <Flex>
          <Row>
            <FileInput
              file={!isEmpty(files) ? files as any : undefined}
              onChange={(event: any) => setFiles(event.target.value)}
              placeholder={t(translations.importExport_noFileSelected) || ''}
              label={t(translations.importExport_Select)}
              multi
            />
            <Button label={t(translations.importExport_upload)} onClick={() => onClickUpload()} disabled={isEmpty(files)} />
          </Row>
        </Flex>
      )}
      {(isUploading || isParsing) && (
      <div>
        <Text>{`${isUploading ? t(translations.importExport_uploading) : t(translations.importExport_parsing)}  ${currentFileName} ${fileProgress}`}</Text>
        <ProgressBar percentage={getPercentage()} showProgressColors width={300} />
      </div>
      )}
    </Flex>
  );

  const modal = nodeListVisible ? nodeListModal : (
    <Modal
      centered
      visible={visible}
    >
      <Dialog
        dialog={{
          heading: t(translations.importExport_fileUpload),
          width: 330,
          content: uploadComponents,
          contentPadding: 'var(--padding-sm)',
          footer: <Button disabled={isUploading || isParsing} label={t(translations.cancel)} onClick={closeUploadModal} />,
          onClose: (!isUploading && !isParsing) ? closeUploadModal : undefined,
        }}
      />
    </Modal>
  );

  return (isModal ? modal : uploadComponents);
};

FileUpload.defaultProps = {
  isModal: false,
  visible: false,
  onClose: undefined,
  initialNodeId: null,
  initialFiles: [],
  onUploadComplete: undefined,
};

const mapStateToProps = ({ entities }: { entities: any }) => ({
  isUploading: entities?.upload.isUploading,
  sourceId: entities?.upload.sourceId,
  progress: entities?.upload.progress,
  parsingStatus: entities?.upload.parsingStatus,
  nodes: entities?.node.nodes,
  isLoading: entities?.node.isLoading,
});

const mapDispatchToProps = {
  uploadFormData,
  getAllNodes,
  resetUploadStatus,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(FileUpload);

export { Container as FileUpload };
